import React, { useState, useEffect } from "react";
import { Slider } from "antd";
import "./VolumeAdjustmentPopup.scss";

const overlayClass = "fixed inset-0 z-[9999] bg-transparent";

const popupClass =
  "absolute z-[10000] w-[250px] -translate-x-1/2 rounded-lg border border-[#e0e0e0] bg-white p-4 font-sans shadow-[0_4px_12px_rgba(0,0,0,0.15)] outline-none max-[480px]:w-[220px] max-[480px]:p-3";

const titleClass = "mb-3 flex items-center justify-center gap-2 text-sm font-semibold leading-tight text-[#333]";

const loadingClass = "animate-pulse text-xs font-normal text-[#666]";

const controlSectionClass =
  "mb-3 flex items-center gap-3 max-[480px]:flex-col max-[480px]:items-stretch max-[480px]:gap-2";

const volumeSliderContainerClass =
  "volume-adjustment-popup__slider relative flex h-5 flex-1 items-center";

const ticksClass =
  "pointer-events-none absolute left-0 right-0 top-1/2 z-[1] h-1 -translate-y-1/2";

const tickClass = "absolute top-0 h-1 w-px -translate-x-1/2 bg-[#e0e0e0]";

const inputRowClass = "flex items-center gap-1 max-[480px]:justify-center";

const volumeInputClass =
  "h-6 w-[50px] rounded border border-[#e0e0e0] bg-white px-1.5 text-center text-xs text-[#333] focus:border-[#000000] focus:outline-none";

const spinnerClass =
  "flex h-6 w-5 flex-col overflow-hidden rounded-r border border-l-0 border-[#e0e0e0]";

const spinnerBtnClass =
  "flex flex-1 items-center justify-center border-none bg-white p-0 text-[8px] leading-none text-[#333] transition-colors duration-200 hover:enabled:bg-[#f5f5f5] active:enabled:bg-[#e8e8e8] disabled:cursor-not-allowed disabled:opacity-50";

const spinnerUpClass = `${spinnerBtnClass} border-b border-[#e0e0e0]`;

const footerClass = "flex items-center justify-between";

const checkboxLabelClass = "flex cursor-pointer items-center gap-1.5 text-xs text-[#333]";

const checkboxInputClass = "h-4 w-4 cursor-pointer accent-[#000000]";

const checkboxTextClass = "select-none";

const confirmBtnClass =
  "cursor-pointer rounded border-none bg-[#000000] px-4 py-1.5 text-xs leading-snug text-white transition-colors hover:bg-[#262626] active:bg-[#141414]";

interface VolumeAdjustmentPopupProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (volume: number, applyToAll: boolean) => void;
  initialVolume?: number;
  position?: {
    top: number;
    left: number;
  };
  loading?: boolean;
  showApplyToAll?: boolean;
}

const VolumeAdjustmentPopup: React.FC<VolumeAdjustmentPopupProps> = ({
  visible,
  onClose,
  onConfirm,
  initialVolume = 0,
  position = { top: 0, left: 0 },
  loading = false,
  showApplyToAll = true,
}) => {
  const [volume, setVolume] = useState(initialVolume);
  const [applyToAll, setApplyToAll] = useState(false);

  useEffect(() => {
    setVolume(initialVolume);
  }, [initialVolume]);

  const handleVolumeChange = (newVolume: number) => {
    const clampedVolume = Math.max(0.5, Math.min(2.0, newVolume));
    setVolume(clampedVolume);
  };

  const handleSliderChange = (value: number) => {
    handleVolumeChange(value);
  };

  const handleSliderAfterChange = (value: number) => {
    handleVolumeChange(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace("x", "");
    const newVolume = parseFloat(numericValue);

    if (!isNaN(newVolume)) {
      handleVolumeChange(newVolume);
    }
  };

  const handleConfirm = () => {
    onConfirm(volume, applyToAll);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className={overlayClass} onClick={onClose}>
      <div
        className={popupClass}
        style={{ top: position.top, left: position.left }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className={titleClass}>
          音量调节
          {loading ? <span className={loadingClass}>加载中...</span> : null}
        </div>

        <div className={controlSectionClass}>
          <div className={volumeSliderContainerClass}>
            <Slider
              min={0.5}
              max={2.0}
              step={0.1}
              value={volume}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
              tooltip={{ open: false }}
              style={{ width: "100%" }}
            />
            <div className={ticksClass}>
              <div className={tickClass} style={{ left: "0%" }} />
              <div className={tickClass} style={{ left: "25%" }} />
              <div className={tickClass} style={{ left: "50%" }} />
              <div className={tickClass} style={{ left: "75%" }} />
              <div className={tickClass} style={{ left: "100%" }} />
            </div>
          </div>

          <div className={inputRowClass}>
            <input type="text" value={`${volume.toFixed(1)}x`} onChange={handleInputChange} className={volumeInputClass} />
            <div className={spinnerClass}>
              <button
                type="button"
                className={spinnerUpClass}
                onClick={() => handleVolumeChange(volume + 0.1)}
                disabled={volume >= 2.0}
              >
                ▲
              </button>
              <button
                type="button"
                className={spinnerBtnClass}
                onClick={() => handleVolumeChange(volume - 0.1)}
                disabled={volume <= 0.5}
              >
                ▼
              </button>
            </div>
          </div>
        </div>

        <div className={footerClass} style={{ justifyContent: showApplyToAll ? "space-between" : "flex-end" }}>
          {showApplyToAll ? (
            <label className={checkboxLabelClass}>
              <input
                type="checkbox"
                className={checkboxInputClass}
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
              <span className={checkboxTextClass}>应用到全部音频。</span>
            </label>
          ) : null}

          <button type="button" className={confirmBtnClass} onClick={handleConfirm}>
            确定
          </button>
        </div>
      </div>
    </div>
  );
};

export default VolumeAdjustmentPopup;
